import { useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTravelText } from '../../app/TravelLocaleContext';
import { workspaceNavGroups } from './workspaceNav';

function formatUnread(count) {
  if (!Number.isFinite(count) || count <= 0) return '';
  if (count > 99) return '99+';
  return String(count);
}

function pathMatches(pathname, item) {
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function isTodayNavItem(item) {
  return /\/today$/.test(String(item.to || ''));
}

function isOverviewNavItem(item) {
  return item.end === true && /\/journeys\/[^/]+$/.test(String(item.to || ''));
}

const N = 'travel.journeyWorkspace.nav';

/** 徽章數字僅供視覺；完整「N 則未讀旅程通知」由 visually-hidden 文字提供 accessible name。 */
function UnreadBadge({ badge, count }) {
  const { tp } = useTravelText();
  if (!badge) return null;
  return (
    <span className="journey-workspace__nav-badge">
      <span aria-hidden="true">{badge}</span>
      <span className="visually-hidden">{` ${tp(`${N}.unread`, count)}`}</span>
    </span>
  );
}

function NavItem({ item, badge, unreadCount, offlineCached = false, todayOfflineAvailable = true }) {
  const { t } = useTravelText();
  const label = item.labelKey ? t(item.labelKey) : item.label;
  const todayItem = isTodayNavItem(item);
  const overviewItem = isOverviewNavItem(item);
  const allowOffline = overviewItem || (todayItem && todayOfflineAvailable);
  const testId = item.testId || (todayItem ? 'nav-today' : undefined);

  if (offlineCached && !allowOffline) {
    return (
      <span
        className="journey-workspace__nav-offline"
        aria-disabled="true"
        title={t(`${N}.networkRequired`)}
        data-testid={todayItem ? 'nav-today-offline' : (testId ? `${testId}-offline` : undefined)}
        data-ia-key={item.iaKey || undefined}
      >
        {label}
        <span className="visually-hidden">{t(`${N}.offlineSuffix`)}</span>
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => (isActive ? 'is-active' : undefined)}
      data-testid={testId}
      data-ia-key={item.iaKey || undefined}
    >
      {label}
      {item.badge === 'notices' ? <UnreadBadge badge={badge} count={unreadCount} /> : null}
    </NavLink>
  );
}

/**
 * AUTH-35 IA groups: Core always open. Secondary groups sit behind an explicit
 * 「所有工具／自由編輯」toggle（aria-expanded，非 hover、非 <details>）。
 * 目前頁面屬於次要群組時自動展開，確保高亮與必要導航不被收起
 * （AUTH-35 曾因收合後子連結不可見而回退，故以路由決定預設狀態）。
 *
 * Slice B: when offlineCached, only Overview (+ Today if usable TODAY snapshot)
 * remain navigable among private journey routes.
 */
export default function JourneyWorkspaceNav({
  journeyId,
  unreadCount = null,
  offlineCached = false,
  todayOfflineAvailable = true,
}) {
  const location = useLocation();
  const { t } = useTravelText();
  const navRef = useRef(null);
  const badge = offlineCached ? '' : formatUnread(unreadCount);
  const groups = workspaceNavGroups(journeyId);
  const toolsId = useId();
  const activeInSecondary = groups.some((group) => !group.alwaysOpen
    && group.items.some((item) => pathMatches(location.pathname, item)));
  const [toolsOpen, setToolsOpen] = useState(activeInSecondary);

  useEffect(() => {
    if (activeInSecondary) setToolsOpen(true);
  }, [activeInSecondary]);

  // Epic 3 Slice 1：沿用 NavLink is-active / aria-current，將 active pill 捲入可見區（不改 active 判定）。
  useEffect(() => {
    const active = navRef.current?.querySelector('a.is-active, a[aria-current="page"]');
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [location.pathname]);

  return (
    <nav
      ref={navRef}
      className="journey-workspace__nav journey-workspace__nav--ia-v2"
      aria-label={t(`${N}.label`)}
      data-testid="journey-workspace-nav"
      data-workspace-ia="v2"
    >
      {groups.filter((group) => group.alwaysOpen).map((group) => (
        <div
          key={group.id}
          className="journey-workspace__nav-group journey-workspace__nav-group--core"
          data-testid={`journey-nav-group-${group.id}`}
        >
          <div className="journey-workspace__nav-links">
            {group.items.map((item) => (
              <NavItem
                key={item.to}
                item={item}
                badge={badge}
                unreadCount={unreadCount}
                offlineCached={offlineCached}
                todayOfflineAvailable={todayOfflineAvailable}
              />
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        className="journey-workspace__tools-toggle"
        aria-expanded={toolsOpen}
        aria-controls={toolsId}
        data-testid="journey-nav-tools-toggle"
        onClick={() => setToolsOpen((open) => !open)}
      >
        {t(toolsOpen ? `${N}.toolsHide` : `${N}.toolsShow`)}
        {!toolsOpen ? <UnreadBadge badge={badge} count={unreadCount} /> : null}
      </button>
      <div id={toolsId} className="journey-workspace__tools" hidden={!toolsOpen} data-testid="journey-nav-tools">
        {groups.filter((group) => !group.alwaysOpen).map((group) => {
          const hasActive = group.items.some((item) => pathMatches(location.pathname, item));

          return (
            <div
              key={group.id}
              className="journey-workspace__nav-group"
              data-testid={`journey-nav-group-${group.id}`}
              data-active-group={hasActive ? 'true' : undefined}
            >
              <div className="journey-workspace__nav-summary">
                {group.labelKey ? t(group.labelKey) : group.label}
                {hasActive ? <span className="journey-workspace__nav-summary-active">{` · ${t(`${N}.current`)}`}</span> : null}
              </div>
              <div
                className="journey-workspace__nav-links"
                data-testid={`journey-nav-group-${group.id}-links`}
                data-ia-key={group.iaKey || undefined}
              >
                {group.items.map((item) => (
                  <NavItem
                    key={item.to}
                    item={item}
                    badge={badge}
                    unreadCount={unreadCount}
                    offlineCached={offlineCached}
                    todayOfflineAvailable={todayOfflineAvailable}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
